import { TestImage } from "@assets/constant/index.constant";
import { Button } from "@components/ui/button";
import { GeneralCard, TableCard } from "@ui/internal/components/card";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { endpoint } from "@services/api.services";
import FilterPopover from "@ui/internal/components/filter-popover";
import {
  formatDate,
  formatNumber,
  showToast,
} from "@utils/helper/index.helper";
import { usePathname } from "@utils/hook/index.hook";
import { QueryMetaData } from "@utils/interface/index.interface";
import { CourseData } from "@utils/interface/journey.interface";
import { useListData } from "@utils/query/index.query";
import { useMetaStore } from "@utils/states/index.state";
import { useEffect } from "react";
import { NavLink, useSearchParams } from "react-router";
import { ColumnList } from "./column/index.column";
import BulkPriceToggle from "./components/BulkPriceToggle";
import CourseConfigureHub from "./configure/CourseConfigureHub";
import CourseForm from "./form/course.form";

interface SelectionProps {
  category_counts: any;
}
const index = () => {
  const pathname = usePathname();

  const { search, date } = useMetaStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, refetch, isPending, error } = useListData<
    QueryMetaData<CourseData, SelectionProps>
  >({
    endpoint: endpoint["course"],
    key: `${pathname}-listing`,
    params: {
      page: searchParams.get("tpage") || 1,
      size: searchParams.get("per") || 10,
      search,
      created_date__gte: date.from ? formatDate(date.from) : "",
      created_date__lte: date.to ? formatDate(date.to) : "",
      ordering: searchParams.get("ordering") || "order",
      course_state_type: searchParams.get("course_state") || "",
      mentor: searchParams.get("mentor-course-linked") || "",
      duration: searchParams.get("duration") || "",
      category:
        searchParams.get("data-category")?.split("-")[0] === "All"
          ? ""
          : searchParams.get("data-category"),
      is_masterclass: pathname == "masterclass-courses",
      is_see: pathname == "see-courses",
      is_plus2: pathname == "plus2-courses",
      is_jrp: pathname == "jrp-courses",
    },
  });
  // groupRefetch
  const groupRefetch = () => {
    refetch();
  };
  // rollback page
  useEffect(() => {
    if (error?.status === 404 && error?.data?.detail === "Invalid page.") {
      searchParams.set("tpage", "1");
      setSearchParams(searchParams);
    }
    console.log("Error", error?.data);
    Object.entries(error?.data ?? {}).forEach(([key, value]) => {
      showToast({
        description: `Failed to create ${key}: ${value}`,
        type: "error",
        title: "Error",
      });
    });
  }, [error]);

  const columns = ColumnList();
  return (
    <>
      <CourseConfigureHub />
      <TableCard
        bulkActions
        inject_bulk_actions={<BulkPriceToggle refetch={groupRefetch} />}
        inject_ui_table_actions={
          <>
            <FilterPopover
              filters={[
                {
                  label: "Mentor",
                  endpointValue: "mentor-course-linked",
                  queries: {
                    is_masterclass:
                      pathname == "masterclass-courses" ? "true" : "false",
                    is_see: pathname == "see-courses" ? "true" : "false",
                    is_plus2: pathname == "plus2-courses" ? "true" : "false",
                    is_jrp: pathname == "jrp-courses" ? "true" : "false",
                  },
                  format: {
                    key: "email",
                    value: "id",
                  },
                },
                {
                  label: "Course state",
                  searchKey: "course_state",
                  radio: true,
                  defaultOptions: [
                    {
                      label: "Ongoing",
                      value: "ongoing",
                    },

                    {
                      label: "Upcoming",
                      value: "upcoming",
                    },
                  ],
                },
                {
                  label: "Duration",
                  endpointValue: "duration",
                  format: {
                    key: "duration",
                    value: "id",
                  },
                },
              ]}
              title="Filter"
              triggerText="Filter"
            />
            {/* <Button variant={"outline"}>Manage Setup</Button> */}

            {["masterclass-courses", "jrp-courses"].includes(pathname) ? (
              <>
                <NavLink to={`/${pathname}/&-batch`}>
                  <Button variant={"defaultoutline"}>
                    Manage Course & Batch
                  </Button>
                </NavLink>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="defaultoutline">Manage Setup</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="start">
                  <DropdownMenuRadioGroup>
                    <NavLink to={"/courses/categories"}>
                      <DropdownMenuItem>Manage Categories</DropdownMenuItem>
                    </NavLink>
                    <NavLink to={"/courses/duration"}>
                      <DropdownMenuItem>Manage Duration</DropdownMenuItem>
                    </NavLink>
                    <NavLink to={"/courses/batch"}>
                      <DropdownMenuItem>Manage Batch</DropdownMenuItem>
                    </NavLink>
                    <NavLink to={"/courses/&-batch"}>
                      <DropdownMenuItem>Manage Course & Batch</DropdownMenuItem>
                    </NavLink>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        }
        table_content={{
          mode: "default",
          type: pathname,
        }}
        form={<CourseForm />}
        formId={pathname}
        tableHeaderData={[
          //This is the Table Header Filter
          {
            id: 1,
            name: "All",
            count: formatNumber(data?.data?.total_count as number) || 0,
          },
          ...Object.keys(data?.data?.category_counts || {}).map(
            (key, index) => ({
              id: index + 1,
              name: key,
              count: formatNumber(
                data?.data?.category_counts?.[
                  key as keyof typeof data.data.category_counts
                ] || 0
              ),
            })
          ),
        ]}
        // showOrientation
        sortableFilter={{
          search,
          created_date__gte: date.from ? formatDate(date.from) : "",
          created_date__lte: date.to ? formatDate(date.to) : "",
          mentor: searchParams.get("mentor") || "",
          duration: searchParams.get("duration") || "",
          category:
            searchParams
              .get("data-category")
              ?.toLocaleLowerCase()
              ?.split("-")[0] === "all"
              ? ""
              : searchParams
                  .get("data-category")
                  ?.toLocaleLowerCase()
                  ?.split("-")[0] || "",
          is_masterclass: ["courses", "jrp-courses"].includes(pathname)
            ? false
            : true,
          is_see: pathname == "see-courses",
          is_plus2: pathname == "plus2-courses",
          is_jrp: pathname == "jrp-courses",
        }}
        grid={
          <GeneralCard
            isChecked={false}
            type={"course"}
            content={{
              images: [TestImage, TestImage, TestImage],
              title: "Aayush Maharjan",
              subtitle: "Frontend Developer",
            }}
            showCheck
            onDelete={() => {}}
            onEdit={() => {}}
          />
        }
        isPending={isPending}
        columns={columns}
        endpoint={endpoint["course"]}
        refetch={groupRefetch}
        rows={data?.data?.results ?? []}
        count={data?.data?.count ?? 10}
      />
    </>
  );
};

export default index;
